import { datadogRum } from "@datadog/browser-rum";
import { RowSelectionState } from "@tanstack/react-table";
import classNames from "classnames";
import compact from "lodash/compact";
import omit from "lodash/omit";
import values from "lodash/values";
import { SubsidiaryModeAlert } from "PFApp/components/subsidiary_mode_alert";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { SidePanelType, useSidePanel, useSidePanelActions } from "PFApp/stores/side_panels_store";
import { Stack } from "PFComponents/containers";
import { Tabs } from "PFComponents/tabs";
import { Tab } from "PFComponents/tabs/tabs.types";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import useStorage from "PFCore/helpers/use_storage";
import { useProfileSkillsFrameworks } from "PFCore/hooks/queries/skills_frameworks/use_profile_skills_frameworks";
import { FeatureFlag, Profile } from "PFCore/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { PageSection } from "../page_section";
import { SkillsBulkEditSidePanel } from "./parts/skills_bulk_edit_side_panel";
import { SkillsSectionActions } from "./parts/skills_section_actions";
import { SkillsByFrameworkView } from "./parts/views/skills_by_framework_view";
import { SkillsByProficiencyView } from "./parts/views/skills_by_proficiency_view";
import { SkillsChartView } from "./parts/views/skills_chart_view";
import { SkillsUngroupedView } from "./parts/views/skills_ungrouped_view";
import css from "./skills_section.module.scss";
import { SkillsPanelTab, SkillsViewCommonProps } from "./skills_section.types";
import { filterSkillsByTerm, getSelectedSkills, getSelectedSkillsIds } from "./skills_section.utils";

type SkillsSectionProps = {
  profile: Profile;
  canEditSkills: boolean;
  isGridView?: boolean;
  className?: string;
};

const DEFAULT_SKILLS_PANEL_TAB = SkillsPanelTab.ByProficiency;

export const SkillsSection = ({ profile, canEditSkills, isGridView, className }: SkillsSectionProps) => {
  const { t } = useTranslation("profiles", { keyPrefix: "show.parts" });
  const { t: tShow } = useTranslation("profiles", { keyPrefix: "show" });
  const [searchParams] = useSearchParams();
  const { isOpen: isBulkEditSidePanelOpen } = useSidePanel(SidePanelType.ProfileBulkEditSkills);
  const { open: openBulkEditSidePanelOpen, close: closeBulkEditSidePanelOpen } = useSidePanelActions(
    SidePanelType.ProfileBulkEditSkills
  );
  const isSkillsFrameworkFeatureEnabled = useIsFeatureEnabled(FeatureFlag.SkillsFramework);
  const isChartFeatureEnabled = useIsFeatureEnabled(FeatureFlag.ShowSkillCategories);

  const { data } = useProfileSkillsFrameworks({
    profileId: profile.id,
    params: { page: 1, perPage: PER_PAGE_UNPAGINATED }
  });
  const skillsFrameworks = data?.entries || [];

  const [searchTerm, setSearchTerm] = useState(searchParams.get("term") ?? "");
  const [tab, setTab] = useStorage<SkillsPanelTab>("skillsSectionTab", DEFAULT_SKILLS_PANEL_TAB);
  const [rowSelectionState, setRowSelectionState] = useState<RowSelectionState>({});
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const nonExistingSelectedSkillsIds = getSelectedSkillsIds(rowSelectionState).filter(
      (id) => !(profile.skills || []).find(({ id: skillId }) => skillId === id)
    );
    if (nonExistingSelectedSkillsIds.length > 0) {
      setRowSelectionState((state) => omit(state, nonExistingSelectedSkillsIds) as RowSelectionState);
    }
  }, [profile.skills]);

  useEffect(() => {
    if (!datadogRum) {
      return;
    }
    const unrankedSkills = (profile.skills || []).filter(({ experience }) => !experience);

    if (unrankedSkills.length > 0) {
      datadogRum.addError(new Error("Unranked skills were found"), { unrankedSkills });
    }
  }, []);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [tab]);

  const hasSkillCategories = useMemo(
    () => profile.skillCategories && profile.skillCategories.length > 0,
    [profile.skillCategories]
  );

  useEffect(() => {
    const isOnUnavailableChartTab =
      tab === SkillsPanelTab.Chart && (!hasSkillCategories || !isChartFeatureEnabled);
    const isOnUnavailableByFrameworkTab =
      tab === SkillsPanelTab.ByFramework && !isSkillsFrameworkFeatureEnabled;
    if (isOnUnavailableChartTab || isOnUnavailableByFrameworkTab) {
      setTab(DEFAULT_SKILLS_PANEL_TAB);
    }
  }, [hasSkillCategories, isSkillsFrameworkFeatureEnabled, isChartFeatureEnabled, tab]);

  const tabs: Tab<SkillsPanelTab>[] = useMemo(
    () =>
      compact([
        {
          id: SkillsPanelTab.ByProficiency,
          title: t("skillsSection.tabs.byProficiency")
        },
        isSkillsFrameworkFeatureEnabled && {
          id: SkillsPanelTab.ByFramework,
          title: t("skillsSection.tabs.byFramework")
        },
        isChartFeatureEnabled && {
          id: SkillsPanelTab.Chart,
          title: t("skillsSection.tabs.byCategory"),
          disabled: !hasSkillCategories
        },
        {
          id: SkillsPanelTab.Ungrouped,
          title: t("skillsSection.tabs.ungrouped")
        }
      ]),
    [hasSkillCategories, isChartFeatureEnabled, isSkillsFrameworkFeatureEnabled, t]
  );

  const filteredSkills = useMemo(
    () => filterSkillsByTerm(profile.skills, searchTerm),
    [searchTerm, profile.skills]
  );

  const currentView = useMemo(() => {
    const commonProps: SkillsViewCommonProps = {
      canEditSkills,
      rowSelectionState,
      setRowSelectionState,
      searchTerm,
      setSearchTerm
    };
    return {
      [SkillsPanelTab.ByProficiency]: (
        <SkillsByProficiencyView skills={filteredSkills} profileId={profile.id} {...commonProps} />
      ),
      [SkillsPanelTab.ByFramework]: <SkillsByFrameworkView profile={profile} {...commonProps} />,
      [SkillsPanelTab.Chart]: <SkillsChartView profile={profile} skills={filteredSkills} {...commonProps} />,
      [SkillsPanelTab.Ungrouped]: (
        <SkillsUngroupedView skills={filteredSkills} profileId={profile.id} {...commonProps} />
      )
    }[tab];
  }, [canEditSkills, rowSelectionState, filteredSkills, profile, searchTerm, tab]);

  const selectedSkillsCount = compact(values(rowSelectionState)).length;
  const isAnySkillSelected = selectedSkillsCount > 0;

  return (
    <PageSection
      role="region"
      aria-label={tShow("skills")}
      qaId="ProfilePage.skills"
      className={classNames(
        css.rootCard,
        { [css.chartView]: tab === SkillsPanelTab.Chart, [css.limitedHeight]: !isGridView },
        className
      )}
      title={t("skillsSection.skillsWithCount", { count: filteredSkills.length })}
      action={
        <SkillsSectionActions
          canEditSkills={canEditSkills}
          searchTerm={searchTerm}
          onTermChange={setSearchTerm}
        />
      }
      skipToSection={{ targetId: "skills-groups", text: t("skipLinks.skillsGroups") }}
    >
      <Stack gap="spacingLg" className={css.tabsAndContentContainer}>
        <Tabs<SkillsPanelTab> activeTab={tab} tabs={tabs} disabled={isAnySkillSelected} onChange={setTab} />
        {isAnySkillSelected && (
          <div className={css.bulkActionsContainer}>
            <SubsidiaryModeAlert
              title={t("skillsSection.bulk.nSkillsSelected", { count: selectedSkillsCount })}
              actions={[
                {
                  icon: "edit",
                  onClick: () => openBulkEditSidePanelOpen({}, true)
                }
              ]}
              onClose={() => setRowSelectionState({})}
              className={css.alert}
            />
          </div>
        )}
        <div className={css.contentContainer} ref={contentRef}>
          {currentView}
        </div>
        {isBulkEditSidePanelOpen && (
          <SkillsBulkEditSidePanel
            profile={profile}
            selectedSkills={getSelectedSkills(profile, skillsFrameworks, rowSelectionState)}
            onSuccess={() => setRowSelectionState({})}
            onClose={closeBulkEditSidePanelOpen}
          />
        )}
      </Stack>
    </PageSection>
  );
};
